/**
 * tripMap/config.js — shared visual constants for the trip map views
 * (`TripOverviewMap`, `RouteMapViewer`, fullscreen variants).
 *
 * b99.10.04 (2026-05-19) — Phase 1 of the map-component unification.
 * Goal: when we change one of these values, BOTH the public trip
 * sheet and the trip-edit per-segment map update together.
 *
 * Marker icons themselves live in `components/maps/mapIcons.js` —
 * this module only owns the *policy* (when to use compact vs full,
 * which color palette per segment, etc.).
 */
import { dotIcon, segmentIcon, ROUTE_COLOR } from "../maps/mapIcons";

/**
 * Per-segment color cycle. Used by `TripOverviewMap` when there are
 * several segments to disambiguate them visually. For single-segment
 * maps (edit view) we always pick `ROUTE_COLOR` so the per-trip color
 * cycling never leaks into the edit experience.
 */
export const SEGMENT_COLORS = [
  "#ea580c", // orange
  "#2563eb", // blue
  "#16a34a", // green
  "#dc2626", // red
  "#8b5cf6", // purple
  "#0891b2", // cyan
  "#ca8a04", // amber
  "#db2777", // pink
];

export function colorForSegment(index = 0) {
  return SEGMENT_COLORS[index % SEGMENT_COLORS.length];
}

/**
 * Marker policy — single source of truth for the inline vs fullscreen
 * teardrop/dot decision. Both maps agree:
 *   - Inline (minimized): `dotIcon` — the small circle. Avoids drowning
 *     the polyline at 250-px height.
 *   - Fullscreen: `segmentIcon` — the teardrop pin.
 *
 * `compactMarkers` is the canonical name carried through the
 * `<SegmentRoutePolyline>` props and the future single-segment
 * editable mode.
 */
export function waypointIconFor({ fullscreen = false, color = ROUTE_COLOR } = {}) {
  return fullscreen ? segmentIcon(color) : dotIcon(color);
}

/**
 * Tooltip offset matched to the marker height — so the label sits
 * above the icon regardless of which one we picked. Inline `dotIcon`
 * is 10×10 (anchor [5,5]); fullscreen `segmentIcon` is 18×24 (anchor
 * [9,24]).
 */
export function tooltipOffsetFor({ fullscreen = false } = {}) {
  return fullscreen ? [0, -24] : [0, -10];
}

/**
 * Elevation polling backoff — exported so admin tooling / diagnostics
 * can reference the same delays.
 */
export const ELEVATION_POLL_DELAYS_MS = [2500, 4000, 6000, 10000, 15000];
